import type { PageHelp } from '../../help/types';

/** Page guide for `/projects/:code/activity` (spec §9.5, §7.3). */
export const activityHelp: PageHelp = {
  title: 'Activity',
  intro:
    'Translation revisions and settings changes of this project in one feed, newest first. Use it to answer “why does this say that now” — the answer is as often a settings change as an edit.',
  sections: ['Choosing the window', 'Reading an entry'],
  steps: [
    // --- Choosing the window ---------------------------------------------------------------
    {
      selector: '[data-help="activity-window"]',
      title: 'Choose how far back to look',
      body: 'Last 24 hours, last 7 days (the default), last 30 days, or everything. The feed only goes back as far as you ask, so widen the window before concluding that nothing happened.',
      section: 'Choosing the window',
      i18n: {
        'de-AT': {
          title: 'Zeitraum wählen',
          body: 'Letzte 24 Stunden, letzte 7 Tage (Standard), letzte 30 Tage oder alles. Der Feed reicht nur so weit zurück, wie Sie es verlangen – erweitern Sie den Zeitraum, bevor Sie schließen, dass nichts passiert ist.',
        },
        'sr-Latn-RS': {
          title: 'Izaberite koliko unazad gledate',
          body: 'Poslednja 24 sata, poslednjih 7 dana (podrazumevano), poslednjih 30 dana ili sve. Feed ide unazad samo onoliko koliko tražite, pa proširite period pre nego što zaključite da se ništa nije desilo.',
        },
      },
    },
    {
      selector: '[data-help="activity-feed"]',
      title: 'Read the feed',
      body: 'Translation revisions and settings changes interleaved by time, newest first. It shows at most the 200 newest entries of the chosen window.',
      section: 'Choosing the window',
      i18n: {
        'de-AT': {
          title: 'Feed lesen',
          body: 'Übersetzungsrevisionen und Einstellungsänderungen, zeitlich ineinander sortiert, die neuesten zuerst. Angezeigt werden höchstens die 200 neuesten Einträge des gewählten Zeitraums.',
        },
        'sr-Latn-RS': {
          title: 'Čitajte feed',
          body: 'Revizije prevoda i izmene podešavanja, poređane zajedno po vremenu, od najnovijih. Prikazuje se najviše 200 najnovijih unosa iz izabranog perioda.',
        },
      },
    },

    // --- Reading an entry ------------------------------------------------------------------
    {
      selector: '[data-help="activity-source"]',
      title: 'See where a change came from',
      body: 'For a translation the chip says where the write came from: `ui` (the web app), `ice` (the in-context editor), `api`, `import`, `ai`, `mt` or `migration`, followed by the key and locale. For a settings change it names the action instead, such as `create`, `update` or `import_revert`, followed by what was changed.',
      section: 'Reading an entry',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Herkunft einer Änderung sehen',
          body: 'Bei einer Übersetzung zeigt der Chip, woher die Änderung kam: `ui` (die Web-App), `ice` (der In-Context-Editor), `api`, `import`, `ai`, `mt` oder `migration`, danach Key und Locale. Bei einer Einstellungsänderung nennt er stattdessen die Aktion, etwa `create`, `update` oder `import_revert`, danach das geänderte Objekt.',
        },
        'sr-Latn-RS': {
          title: 'Pogledajte odakle je izmena došla',
          body: 'Kod prevoda oznaka pokazuje odakle je upis došao: `ui` (veb aplikacija), `ice` (editor u kontekstu), `api`, `import`, `ai`, `mt` ili `migration`, a zatim ključ i locale. Kod izmene podešavanja umesto toga navodi akciju, na primer `create`, `update` ili `import_revert`, a zatim šta je izmenjeno.',
        },
      },
    },
    {
      selector: '[data-help="activity-revision"]',
      title: 'Read the new status and revision',
      body: 'The status the cell had after this write — for example draft, reviewed or machine — and its revision number (r3). Every write to a cell raises its revision by one, which is how an edit made from an outdated screen is detected as a conflict.',
      section: 'Reading an entry',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Neuen Status und Revision lesen',
          body: 'Der Status der Zelle nach dieser Änderung – etwa draft, reviewed oder machine – und ihre Revisionsnummer (r3). Jede Änderung an einer Zelle erhöht ihre Revision um eins; daran wird eine Bearbeitung aus einer veralteten Ansicht als Konflikt erkannt.',
        },
        'sr-Latn-RS': {
          title: 'Pročitajte novi status i reviziju',
          body: 'Status ćelije posle ovog upisa – na primer draft, reviewed ili machine – i broj njene revizije (r3). Svaki upis u ćeliju povećava reviziju za jedan, i po tome se izmena sa zastarelog ekrana prepoznaje kao konflikt.',
        },
      },
    },
    {
      selector: '[data-help="activity-value"]',
      title: 'Read the value that was written',
      body: 'The text the cell held after the change, line breaks included. An entry that reads “cleared” means the cell was emptied.',
      section: 'Reading an entry',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Geschriebenen Wert lesen',
          body: 'Der Text, den die Zelle nach der Änderung enthielt, samt Zeilenumbrüchen. Ein Eintrag mit „cleared“ bedeutet, dass die Zelle geleert wurde.',
        },
        'sr-Latn-RS': {
          title: 'Pročitajte upisanu vrednost',
          body: 'Tekst koji je ćelija imala posle izmene, zajedno sa prelomima redova. Unos sa „cleared“ znači da je ćelija ispražnjena.',
        },
      },
    },
    {
      selector: '[data-help="activity-note"]',
      title: 'Read the note on a write',
      body: 'Some writes carry a note: an import shows `batch:<id>` and a revert `revert:<id>`, the batch number from the Imports page, and an AI write names the model that produced the text.',
      section: 'Reading an entry',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Notiz zu einer Änderung lesen',
          body: 'Manche Änderungen tragen eine Notiz: Ein Import zeigt `batch:<id>` und ein Revert `revert:<id>` – die Batch-Nummer von der Seite „Imports“ –, ein AI-Eintrag nennt das Modell, das den Text erzeugt hat.',
        },
        'sr-Latn-RS': {
          title: 'Pročitajte belešku uz upis',
          body: 'Neki upisi nose belešku: uvoz prikazuje `batch:<id>`, a revert `revert:<id>` – broj batch-a sa stranice „Imports“ – dok AI upis navodi model koji je napravio tekst.',
        },
      },
    },
    {
      selector: '[data-help="activity-actor"]',
      title: 'See who changed it and when',
      body: 'The person who made the change, by name and email, and the time it happened, shown in your browser’s local time. Changes made by a background job read “System” instead of a person, such as “System (ai worker)”, and an API token reads “API token #” with its number.',
      section: 'Reading an entry',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Sehen, wer es wann geändert hat',
          body: 'Die Person, die die Änderung vorgenommen hat, mit Name und E-Mail, und der Zeitpunkt, in der lokalen Zeit Ihres Browsers. Änderungen eines Hintergrundjobs zeigen statt einer Person „System“, etwa „System (ai worker)“, ein API-Token zeigt „API token #“ mit seiner Nummer.',
        },
        'sr-Latn-RS': {
          title: 'Pogledajte ko je i kada menjao',
          body: 'Osoba koja je napravila izmenu, imenom i e-adresom, i vreme kada se desila, prikazano u lokalnom vremenu vašeg pregledača. Izmene pozadinskog posla umesto osobe prikazuju „System“, na primer „System (ai worker)“, a API token „API token #“ sa svojim brojem.',
        },
      },
    },
  ],
  i18n: {
    'de-AT': {
      title: 'Aktivität',
      intro:
        'Übersetzungsrevisionen und Einstellungsänderungen dieses Projekts in einem Feed, die neuesten zuerst. Damit beantworten Sie die Frage „Warum steht da jetzt das?“ – die Antwort ist ebenso oft eine Einstellungsänderung wie eine Bearbeitung.',
      sections: {
        'Choosing the window': 'Zeitraum wählen',
        'Reading an entry': 'Einen Eintrag lesen',
      },
    },
    'sr-Latn-RS': {
      title: 'Aktivnost',
      intro:
        'Revizije prevoda i izmene podešavanja ovog projekta u jednom feed-u, od najnovijih. Koristite ga da odgovorite na pitanje „zašto sada ovde piše ovo“ – odgovor je jednako često izmena podešavanja koliko i izmena teksta.',
      sections: {
        'Choosing the window': 'Izbor perioda',
        'Reading an entry': 'Čitanje unosa',
      },
    },
  },
};
